/**
 * React component that wraps with formik form and handles errors checks and scroll to input
 * Wrap Create/Edit/Import load components with this form component to handle errors and scroll to error input
 *  */

import React from 'react';
import { Formik, Form, FormikValues, FormikErrors } from 'formik';

import LoadUtils from '../../utils/loadUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import DateUtils from '../../utils/dateUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import { logRocketTrack } from '../../utils/logRocketUtils';
import { useGetCreateLoadConfigQuery } from '../../redux/api/load/loadApi';
import { useAppSelector } from '../../redux/hooks';
import { CreateLoadFields, LoadClientType } from '../../redux/api/load/load.model';
import { InvoiceServiceUnit } from '../../redux/api/invoiceService/invoiceService.model';
import { RATE_REGEX } from '../../pages/CreateInvoice/CreateInvoice';
import { Driver, DRIVER_TYPE } from '../../redux/api/driver/driver.model';

interface TwLoadFormProps {
  /**
   * Function that handles form submission
   */
  onSubmit: () => void;

  /**
   * reference to the form
   */
  formRef: React.RefObject<HTMLFormElement>;

  children: React.ReactNode;
}

const TwLoadForm: React.FC<TwLoadFormProps> = ({ onSubmit, children, formRef }) => {
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const {
    order_rates: load_rates,
    provider_stops,
    rate,
    selected_customer,
    selected_primary_client,
    show_advanced_rates,
    status,
    is_brokered_order,
    payable_amount,
    trips
  } = useAppSelector((state) => state.orders);

  const enableAppointmentWindow = getTenantSetting('enable-appointment-window', false);
  const isRateOptional = getTenantSetting('rate-optional');
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const { addNotification } = useNotifications();
  const { data: fieldsConfig = [] } = useGetCreateLoadConfigQuery();

  const getErrorInfo = (values: FormikValues): [FormikErrors<FormikValues>, string | undefined] => {
    const errorDateMessage = 'Date validation failed: please ensure date is correct.';
    const errorTimeMessage = 'Time validation failed: please ensure time is correct.';
    const errors: any = {};
    const reqFields: CreateLoadFields[] = LoadUtils.getRequiredFields(status, fieldsConfig);

    if (reqFields.length === 0) return [{}, undefined];

    let errorInputName: string | undefined = undefined;

    // stop dates check
    if (
      (reqFields.includes('start_date') || reqFields.includes('end_date')) &&
      provider_stops &&
      provider_stops.length > 0
    ) {
      for (let i = 0; i < provider_stops.length; i++) {
        const stop = provider_stops[i];
        const nextStop = provider_stops[i + 1];
        if (!stop.provider_date) {
          errors[`stop-date-${stop.order}`] = `Missing required field: Stop ${stop.order} date`;
          errorInputName = `stop-date-${stop.order}`;
          break;
        }

        if (enableAppointmentWindow) {
          // All validation for appointment window enabled

          // Validate if stop has provider_date and next stop has provider_date
          if (nextStop && nextStop.provider_date && stop.provider_date && nextStop.provider_date) {
            const currentStopDate = DateUtils.getDateInLocale(new Date(stop.provider_date));
            const nextStopDate = DateUtils.getDateInLocale(new Date(nextStop.provider_date));

            // Throw error if next stop date is less than current stop date
            if (nextStopDate < currentStopDate) {
              errors[`stop-date-${nextStop.order}`] = errorDateMessage;
              errorInputName = `stop-date-${nextStop.order}`;
              logRocketTrack('Load stop date validation failed', {
                action: 'Stop date validation failed',
                severity: 'low',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              break;
            }
          }

          // Throw error if time to is less than time from
          if (stop.provider_time_from && stop.provider_time_to) {
            if (stop.provider_time_to.setSeconds(0, 0) < stop.provider_time_from.setSeconds(0, 0)) {
              errors[`stop-time-to-${stop.order}`] = errorTimeMessage;
              errorInputName = `stop-time-to-${stop.order}`;
              logRocketTrack('Load stop time validation failed', {
                action: 'Stop time validation failed',
                severity: 'low',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              break;
            }
          }

          // Throw error if time to is provided and time from is not
          if (!stop.provider_time_from && stop.provider_time_to) {
            errors[`stop-time-from-${stop.order}`] = errorTimeMessage;
            errorInputName = `stop-time-from-${stop.order}`;
            logRocketTrack('Load stop time validation failed', {
              action: 'Stop time validation failed',
              severity: 'low',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            break;
          }

          // If stop has prvider_date and provider_time_from,
          // check if there exists any previous stop with same date and time from in order to test if
          // time from of stop is greater than time from of previous stop
          // Throw error if any previous stop has same date and time from greater than current stop time from
          if (stop.provider_date && stop.provider_time_from) {
            // check if there exists any previous stop with same date and time from in order to test if it is a duplicate
            const previousStops = provider_stops.slice(0, i);
            const isThereAnyPreviousStopWithSameDateAndTimeFromGrater = previousStops.some((s) => {
              // Temporarily return false to fix: https://app.clickup.com/t/10558201/TW-5340
              return false;
              // s.provider_date &&
              // stop.provider_date &&
              // DateUtils.validateSameDate(s.provider_date, stop.provider_date)
              // &&
              // s.provider_time_from &&
              // stop.provider_time_from &&
              // s.provider_time_from.setSeconds(0, 0) > stop.provider_time_from.setSeconds(0, 0)
            });

            const isThereAnyPreviousStopWithSameDateAndTimeToGrater = previousStops.some((s) => {
              // Temporarily return false to fix: https://app.clickup.com/t/10558201/TW-5340
              return false;
              // s.provider_date &&
              // stop.provider_date &&
              // DateUtils.validateSameDate(s.provider_date, stop.provider_date)
              // &&
              // s.provider_time_to &&
              // stop.provider_time_from &&
              // s.provider_time_to.setSeconds(0, 0) > stop.provider_time_from.setSeconds(0, 0)
            });

            if (
              isThereAnyPreviousStopWithSameDateAndTimeFromGrater ||
              isThereAnyPreviousStopWithSameDateAndTimeToGrater
            ) {
              errors[`stop-time-from-${stop.order}`] = errorTimeMessage;
              errorInputName = `stop-time-from-${stop.order}`;
              logRocketTrack('Load stop date validation failed', {
                action: 'Stop date validation failed',
                severity: 'low',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              break;
            }
          }
        } else {
          // All validation for appointment window disabled

          // Validate if stop has provider_date and next stop has provider_date
          if (nextStop && nextStop.provider_date && stop.provider_date && nextStop.provider_date) {
            const currentStopDate = DateUtils.getDateInLocale(new Date(stop.provider_date));
            const nextStopDate = DateUtils.getDateInLocale(new Date(nextStop.provider_date));

            // Throw error if next stop date is less than current stop date
            if (nextStopDate < currentStopDate) {
              errors[`stop-date-${nextStop.order}`] = errorDateMessage;
              errorInputName = `stop-date-${nextStop.order}`;
              logRocketTrack('Load stop date validation failed', {
                action: 'Stop date validation failed',
                severity: 'low',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              break;
            }
          }

          // If stop has prvider_date and provider_time_from,
          // check if there exists any previous stop with same date and time from in order to test if
          // time from of stop is greater than time from of previous stop
          // Throw error if any previous stop has same date and time from greater than current stop time from
          if (stop.provider_date && stop.provider_time_from) {
            // check if there exists any previous stop with same date and time from in order to test if it is a duplicate
            const previousStops = provider_stops.slice(0, i);
            const isThereAnyPreviousStopWithSameDateAndTimeFromGrater = previousStops.some((s) => {
              // Temporarily return false to fix: https://app.clickup.com/t/10558201/TW-5340
              return false;
              // s.provider_date &&
              // stop.provider_date &&
              // DateUtils.validateSameDate(s.provider_date, stop.provider_date)
              // &&
              // s.provider_time_from &&
              // stop.provider_time_from &&
              // s.provider_time_from.setSeconds(0, 0) > stop.provider_time_from.setSeconds(0, 0)
            });

            if (isThereAnyPreviousStopWithSameDateAndTimeFromGrater) {
              errors[`stop-time-from-${stop.order}`] = errorTimeMessage;
              errorInputName = `stop-time-from-${stop.order}`;
              logRocketTrack('Load stop date validation failed', {
                action: 'Stop date validation failed',
                severity: 'low',
                tenant_id: userData?.tenant?.id,
                company_name: userData?.tenant?.company_name,
                email: userData?.email_address,
                user_first_name: userData?.first_name,
                user_last_name: userData?.last_name
              });
              break;
            }
          }
        }
      }
      if (errorInputName) return [errors, errorInputName];
    }

    // stop times check
    if (
      provider_stops &&
      provider_stops.length > 0 &&
      provider_stops.some(
        (s) =>
          // Validate if s.provider_time_from and s.provider_time_to have correct HH:MM format.
          (s.provider_time_from &&
            s.provider_time_from !== null &&
            !DateUtils.validateTimeFromDate(s.provider_time_from)) ||
          (s.provider_time_to &&
            s.provider_time_to !== null &&
            !DateUtils.validateTimeFromDate(s.provider_time_to))
      )
    ) {
      for (let i = 0; i < provider_stops.length; i++) {
        const stop = provider_stops[i];
        if (!!stop.provider_time_from && !DateUtils.validateTimeFromDate(stop.provider_time_from)) {
          errors[
            `stop-time-from-${stop.order}`
          ] = `The start time for Stop #${stop.order} must be in hh:mm format`;
          errorInputName = `stop-time-from-${stop.order}`;
          break;
        }

        if (!!stop.provider_time_to && !DateUtils.validateTimeFromDate(stop.provider_time_to)) {
          errors[
            `stop-time-to-${stop.order}`
          ] = `The end time for Stop #${stop.order} must be in hh:mm format`;
          errorInputName = `stop-time-to-${stop.order}`;
          break;
        }
      }
      if (errorInputName) return [errors, errorInputName];
    }

    // stop check in and out times (checks that in time is not after out time)
    if (provider_stops && provider_stops.length > 0) {
      for (let i = 0; i < provider_stops.length; i++) {
        const stop = provider_stops[i];

        const stopInDate =
          stop.provider_check_in_date &&
          stop.provider_check_in_time &&
          DateUtils.getISODate(stop.provider_check_in_date, stop.provider_check_in_time);

        const stopOutDate =
          stop.provider_check_out_date &&
          stop.provider_check_out_time &&
          DateUtils.getISODate(stop.provider_check_out_date, stop.provider_check_out_time);

        const stop_in_time = stopInDate && new Date(stopInDate);
        const stop_out_time = stopOutDate && new Date(stopOutDate);

        // Check that check in time and check out time are not in the future
        // if (stop_in_time && stop_in_time > new Date()) {
        //   errors[
        //     `stop-check-in-time-${stop.order}`
        //   ] = `Please adjust Stop #${stop.order}, 'actual in' time cannot be in the future`;
        //   errorInputName = `stop-check-in-time-${stop.order}`;
        //   break;
        // }

        // if (stop_out_time && stop_out_time > new Date()) {
        //   errors[
        //     `stop-check-out-time-${stop.order}`
        //   ] = `Please adjust Stop #${stop.order}, 'actual out' time cannot be in the future`;
        //   errorInputName = `stop-check-out-time-${stop.order}`;
        //   break;
        // }

        // // We need to manually set seconds to 0 to compare times, because same hour and minutes can return different ms
        // if (
        //   stop_in_time &&
        //   stop_out_time &&
        //   stop_in_time.setSeconds(0, 0) > stop_out_time.setSeconds(0, 0)
        // ) {
        //   logRocketTrack('Load build', {
        //     action: 'Stop check in and out times validation failed (in time is after out time)',
        //     severity: 'low',
        //     tenant_id: userData?.tenant?.id,
        //     company_name: userData?.tenant?.company_name,
        //     email: userData?.email_address,
        //     user_first_name: userData?.first_name,
        //     user_last_name: userData?.last_name
        //   });
        //   errors[
        //     `stop-check-in-time-${stop.order}`
        //   ] = `Please adjust Stop #${stop.order}, 'actual out' time cannot be earlier than 'actual in' time`;
        //   errorInputName = `stop-check-in-time-${stop.order}`;
        //   break;
        // }
      }
    }

    // shipper rate check
    if (!isRateOptional && rate === undefined && !show_advanced_rates) {
      errors.shipper_rate = 'Missing required field: Rate';
      errorInputName = 'shipper-rate';
      return [errors, errorInputName];
    }

    // payable amount check, only if rate is required and is a brokered load
    if (!isRateOptional && payable_amount === undefined && is_brokered_order) {
      errors.payable_amount = 'Missing required field: Payable Amount';
      errorInputName = 'payable_amount';
      return [errors, errorInputName];
    }

    // shipper rate format check: 999999.9999 or -999999.9999
    if (
      rate !== undefined &&
      rate > 0 &&
      !RATE_REGEX.test(rate.toString()) &&
      !show_advanced_rates
    ) {
      errors.shipper_rate = 'Rate format error: rate must be in 999999.9999 format';
      errorInputName = 'shipper-rate';
      return [errors, errorInputName];
    }

    const regexpPayableAmount = new RegExp(
      /^(0*[1-9]\d{0,9}(\.\d{0,2})?0*|0*\.([0-9]|\d[0-9]|[0-9][0-9][0-9]\d)0*)$/gm
    );
    /* 
    Examples of valid payable amounts:
    0.8996
    .8690
    000.13
    0.2
    .1
    1
    1.2
    20.78
    20.0
    20.00
    13456
    */
    // Payable amount format check no negative numbers and up to 4 decimal places
    if (payable_amount !== undefined) {
      const isPayableAmountValid = regexpPayableAmount.test(payable_amount.toString());

      if (payable_amount !== undefined && is_brokered_order && !isPayableAmountValid) {
        errors.payable_amount =
          'Payable amount must be a positive value and format accepted is 99999.9999';
        errorInputName = 'payable_amount';
        return [errors, errorInputName];
      }
    }

    // empty advance rate check
    if (load_rates.length === 0 && show_advanced_rates && !isRateOptional) {
      errors['add-rate'] = `Missing required field: Rate`;
      errorInputName = 'add-rate';

      return [errors, errorInputName];
    }

    // filter load rates to remove those place holders with no data (i.e., just an ui_id)
    const nonPlaceholderRates = load_rates.filter(
      (r) =>
        r.invoice_service_id ||
        r.qbo_service_id ||
        r.unit ||
        r.rate ||
        (r.unit !== InvoiceServiceUnit.FLAT ? r.quantity : false)
    );

    // advanced rate check
    if (
      (nonPlaceholderRates.length === 0 && show_advanced_rates && !isRateOptional) ||
      (nonPlaceholderRates.length > 0 &&
        show_advanced_rates &&
        load_rates.some(
          (r) =>
            (!r.invoice_service_id && !r.qbo_service_id) ||
            !r.unit ||
            r.rate == null ||
            Number.isNaN(r.rate) ||
            (r.unit !== InvoiceServiceUnit.FLAT ? !r.quantity : false)
        ))
    ) {
      const failedInputs = load_rates
        .filter(
          (r) =>
            (!r.invoice_service_id && !r.qbo_service_id) ||
            !r.unit ||
            r.rate == null ||
            Number.isNaN(r.rate) ||
            (r.unit !== InvoiceServiceUnit.FLAT ? !r.quantity : false)
        )
        .map((fi) => ({
          ui_id: fi.ui_id,
          service: (!fi.invoice_service_id && !fi.qbo_service_id) as boolean,
          unit: !fi.unit,
          rate: fi.rate == null || Number.isNaN(fi.rate),
          qty: !fi.quantity
        }));

      let inputName = '';
      let keyName = '';

      Object.entries(failedInputs[0]).forEach(([key, value]) => {
        if (inputName) return;

        if (key !== 'ui_id' && value) {
          inputName = `${key}-${failedInputs[0].ui_id}`;
          keyName = key;
        }
      });

      errors[inputName] = `Missing required field: Rate (${
        keyName === 'qty' ? 'quantity' : keyName
      })`;
      errorInputName = inputName;
      return [errors, errorInputName];
    }

    // advanced rate format check: 99999.9999
    if (
      load_rates.length > 0 &&
      load_rates.some((r) => r.rate && !RATE_REGEX.test(r.rate.toString())) &&
      show_advanced_rates
    ) {
      const failedInputs = load_rates
        .filter((r) => r.rate && !RATE_REGEX.test(r.rate.toString()))
        .map((fi) => ({
          ui_id: fi.ui_id,
          rate: fi.rate && !RATE_REGEX.test(fi.rate.toString())
        }));

      let inputName = '';
      let keyName = '';

      Object.entries(failedInputs[0]).forEach(([key, value]) => {
        if (inputName) return;

        if (key !== 'ui_id' && value) {
          inputName = `${key}-${failedInputs[0].ui_id}`;
          keyName = key;
        }
      });

      errors[inputName] = `Rate format error: rate must be in 99999.9999 format`;
      errorInputName = inputName;
      return [errors, errorInputName];
    }

    // customer check
    if (reqFields.includes('customer') && !selected_customer) {
      errors.customer = 'Missing required field: Customer';
      errorInputName = 'customer';
      return [errors, errorInputName];
    }

    // check if all trips have at least two stops
    if (trips.length > 0 && trips.some((t) => t.provider_stops.length < 2)) {
      errors.trips = 'Some trips have less than two stops';
      errorInputName = 'trips';
      return [errors, errorInputName];
    }

    // check if owner operator is selected, then driver must exist
    if (trips.some((t) => t.selected_owner_operator)) {
      // check if a driver id exist
      if (trips.some((t) => t.selected_drivers.some((d) => !d.id))) {
        errors.drivers =
          'You must select a driver before saving, trips with owner operator must have a driver';
        errorInputName = 'driver';
        return [errors, errorInputName];
      }

      // check each trip and check if team drivers has the same owner operator
      trips.forEach((t) => {
        if (t.selected_owner_operator) {
          const teamDrivers = t.selected_drivers;
          const ownerOperatorId = t.selected_owner_operator;

          // if driver type is Owner Operator driver and owner operator id is not the same as the trip owner operator id, throw error
          if (
            teamDrivers.some(
              (d) =>
                d.autocompleteDriver &&
                typeof d.autocompleteDriver !== 'string' &&
                (d.autocompleteDriver.value as Driver).type === DRIVER_TYPE.OWNER_OPERATOR_DRIVER &&
                (d.autocompleteDriver.value as Driver).owner_operator_id !== ownerOperatorId
            )
          ) {
            errors.drivers = `Team drivers on trip ${t.order} must belong to the same Owner Operator`;
            errorInputName = 'driver';
            return [errors, errorInputName];
          }
        }
      });
    }

    return [errors, errorInputName];
  };

  const scrollToView = (identifier: string, isId = false): void => {
    const input = document.querySelector(isId ? `#${identifier}` : `input[name=${identifier}]`);

    input?.scrollIntoView({
      block: 'center'
      //  behavior: 'smooth' // smooth scroll is not working properly see discussion here:
      // https://github.com/facebook/react/issues/23396
    });
  };

  const validate = (values: FormikValues): void | FormikErrors<FormikValues> => {
    const [errors, errorInputName] = getErrorInfo(values);

    if (errorInputName) {
      const errorMessages = Object.values(errors);
      if (errorMessages && errorMessages.length > 0) {
        addNotification({
          message: errorMessages[0] as string,
          variant: 'error'
        });
      }
      scrollToView(errorInputName, errorInputName == 'add-rate' ? true : false);
    }
    return errors;
  };

  return (
    <Formik
      initialValues={{}}
      validate={validate}
      onSubmit={(): void => {
        onSubmit();
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
    >
      <Form ref={formRef}>{children}</Form>
    </Formik>
  );
};

export default TwLoadForm;
